import React, { useMemo } from "react";
import zxcvbn from "zxcvbn";

interface PasswordStrengthMeterProps {
  password: string;
  minScore?: number;
}

const strengthLabels = ["Very weak", "Weak", "Fair", "Strong", "Very strong"];
const strengthColors = [
  "bg-red-500",
  "bg-orange-500",
  "bg-yellow-500",
  "bg-lime-500",
  "bg-green-500",
];

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  minScore = 4,
}) => {
  const result = useMemo(() => {
    if (!password) return null;
    return zxcvbn(password);
  }, [password]);

  if (!password || !result) {
    return null;
  }

  const { score, feedback } = result;
  const isStrong = score >= minScore;

  return (
    <div className="mt-2">
      {/* strength bar */}
      <div className="flex gap-1 mb-1">
        {[0, 1, 2, 3, 4].map((level) => (
          <div
            key={level}
            className={`h-1 flex-1 rounded-full transition-colors ${
              level <= score ? strengthColors[score] : "bg-gray-200"
            }`}
          />
        ))}
      </div>

      {/* label and feedback */}
      <div className="flex justify-between items-start text-sm">
        <span
          className={`font-medium ${isStrong ? "text-green-600" : "text-gray-600"}`}
        >
          {strengthLabels[score]}
        </span>
      </div>

      {/* suggestions */}
      {!isStrong && (feedback.warning || feedback.suggestions.length > 0) && (
        <div className="mt-1 text-sm text-gray-500">
          {feedback.warning && <p>{feedback.warning}</p>}
          {feedback.suggestions.length > 0 && (
            <p>{feedback.suggestions[0]}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthMeter;

export const getPasswordScore = (password: string): number => {
  if (!password) return 0;
  return zxcvbn(password).score;
};
